import { useCallback, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Archive, FolderInput, Trash2, Share2, ExternalLink } from 'lucide-react';
import { api, apiFetch } from '../lib/api';
import { domainOf, titleOf } from '../lib/url';
import { useScrollProgress } from '../lib/useScrollProgress';
import { useReaderActions } from '../lib/useReaderActions';
import { Button } from '../components/Button';
import { MediaBody } from '../components/MediaBody';
import { ShareDialog } from '../components/ShareDialog';
import { HighlightsPanel } from '../components/HighlightsPanel';
import { Article } from '../components/ArticleCard';
import type { Highlight } from '@grove/api-client';

interface FullArticle extends Article {
  content?: string | null;
  highlights?: Highlight[];
  transcript?: Array<{ start: number; dur: number; text: string }> | null;
}

export default function ReaderPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const { data: article, isLoading } = useQuery<FullArticle>({
    queryKey: ['article', id],
    // touch=1 stamps lastAccessedAt — this fetch is a real reader open.
    queryFn: () => apiFetch(`/articles/${id!}?touch=1`),
  });

  const patchArticle = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch(`/articles/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['articles'] }),
  });

  // Video/pdf own their progress (watch point / page) — scroll must not
  // fight them, so both restore and writes are article-type only.
  const isMedia = article?.type === 'video' || article?.type === 'pdf';

  const { liveProgress, onScroll } = useScrollProgress({
    articleId: id ?? '',
    savedProgress: article?.readingProgress,
    status: article?.status ?? 'unread',
    contentReady: !isMedia && !!article?.content,
  });

  useEffect(() => {
    // A filed article is released/read-only — don't write reading progress to it.
    if (article?.status === 'filed' || isMedia) return;
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [onScroll, article?.status, isMedia]);

  // Auto-promote 'unread' → 'reading' when the article opens. Mirrors the
  // ReaderPanel behavior in Queue.tsx so the full-page reader doesn't leave
  // articles stuck in the Unread filter after the user starts reading.
  useEffect(() => {
    if (article?.status === 'unread' && id) {
      patchArticle.mutate({ status: 'reading' });
    }
    // Intentionally only depends on the article id so we don't re-fire after
    // optimistic updates inside the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, article?.status === 'unread']);

  const addHighlight = useMutation({
    mutationFn: (text: string) => api.addHighlight(id!, { text }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['article', id] }),
  });

  const onMouseUp = useCallback(() => {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed) return;
    const text = sel.toString().trim();
    if (text.length < 5) return;
    addHighlight.mutate(text);
    sel.removeAllRanges();
  }, [addHighlight]);

  const navigate = useNavigate();
  const actions = useReaderActions(
    { id: id ?? '', url: article?.url ?? '', title: article ? titleOf(article) : null },
    { onDone: () => navigate('/queue') },
  );

  if (isLoading) return (
    <div className="min-h-screen flex items-center justify-center bg-surface-bg">
      <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!article) return (
    <div className="min-h-screen flex items-center justify-center bg-surface-bg">
      <p className="text-text-secondary">Article not found.</p>
    </div>
  );

  const domain = domainOf(article.url);
  // A filed article has graduated into the user's own vault and been released
  // by Grove. Show Grove's stored copy read-only — no lifecycle actions, no
  // new highlights — with a banner pointing at the vault file.
  const isFiled = article.status === 'filed';

  return (
    <div className="min-h-screen bg-surface-bg">
      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 h-0.5 z-50" style={{ background: '#1A1A2E' }}>
        <div className="h-full transition-all" style={{ width: `${liveProgress}%`, background: '#8B5CF6' }} />
      </div>

      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-white/10 px-4 py-3 flex items-center gap-4 bg-surface-0">
        <Link to="/queue" className="text-sm text-gray-400 hover:text-white transition-colors flex-shrink-0 inline-flex items-center gap-1.5">
          <ArrowLeft size={14} /> Queue
        </Link>
        <p className="text-sm font-medium text-white flex-1 truncate">{titleOf(article)}</p>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isFiled ? (
            <span className="text-xs px-2.5 py-1 rounded-md text-violet-300 border border-violet-500/30 inline-flex items-center gap-1.5">
              <FolderInput size={13} /> Filed
            </span>
          ) : (
            <>
              <Button size="sm" variant="secondary" onClick={actions.archive} disabled={actions.isPending}>
                <span className="inline-flex items-center gap-1.5"><Archive size={14} /> Archive</span>
              </Button>
              <Button size="sm" variant="secondary" onClick={actions.fileTo} disabled={actions.isPending}>
                <span className="inline-flex items-center gap-1.5"><FolderInput size={14} /> File to…</span>
              </Button>
              <Button size="sm" variant="secondary" onClick={actions.remove} disabled={actions.isPending}>
                <span className="inline-flex items-center gap-1.5"><Trash2 size={14} /> Delete</span>
              </Button>
              <Button size="sm" variant="secondary" onClick={actions.share}>
                <span className="inline-flex items-center gap-1.5"><Share2 size={14} /> Share</span>
              </Button>
            </>
          )}
        </div>
      </header>

      {/* Article */}
      <article className="max-w-2xl mx-auto px-4 py-10">
        {isFiled && (
          <div className="mb-8 rounded-lg border border-violet-500/30 bg-violet-500/10 p-4">
            <p className="text-sm text-violet-100">
              Filed to <span className="font-mono text-violet-200">{article.vaultPath ?? 'your vault'}</span> — this is Grove's
              saved copy and may not reflect edits made in your vault since it left Grove.
            </p>
            {article.vaultPath && (
              // vaultPath is the destination *folder*, not the note path, and we
              // don't know the plugin's sanitized filename — search by title,
              // which reliably surfaces the note in Obsidian.
              <a
                href={`obsidian://search?query=${encodeURIComponent(titleOf(article))}`}
                className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-violet-300 hover:text-violet-200 transition-colors"
              >
                <ExternalLink size={14} /> Open in Obsidian
              </a>
            )}
          </div>
        )}
        <h1 className="text-3xl font-bold text-white mb-4 font-reader">{titleOf(article)}</h1>
        {article.author && (
          <p className="text-sm mb-8 text-text-secondary">
            By {article.author} {article.siteName && `· ${article.siteName}`}
            {article.readingTime && ` · ${article.readingTime} min read`}
          </p>
        )}
        <MediaBody article={article} domain={domain} onMouseUp={isFiled ? undefined : onMouseUp} />

        <HighlightsPanel
          articleId={article.id}
          highlights={article.highlights ?? []}
          readOnly={isFiled}
        />
      </article>

      <ShareDialog
        article={{
          id: article.id,
          title: titleOf(article),
          author: article.author,
          siteName: article.siteName,
          image: article.image,
          wordCount: article.wordCount,
        }}
        open={actions.shareOpen}
        onClose={actions.closeShare}
      />
    </div>
  );
}

